import _ from 'lodash'
import CompareMixin from './components/filters/compare-mixin'
import clientAttributeLabel from './utils/client-attribute-label'
import { filters } from './constants'

function getComparisonLabel(comparisonMode, comparisonModes) {
  const mode = _.find(comparisonModes, { value: comparisonMode })
  return (mode && mode.label) || ''
}

function titleize(string, condition = true) {
  return condition ? _.startCase(string) : string
}

const workflowProgressFilter = {
  type: filters.WORKFLOW_PROGRESS,
  name_key: 'workflow_progress',
  ledgerRequired: true,
  hasModalHelp: true,

  comparisonModes() {
    return [
      ...CompareMixin.computed.numberComparisonModes(),
      ...CompareMixin.computed.showAllComparisonMode(),
    ]
  },

  activeSubtitle(settings, getters) {
    const { comparisonMode, firmWorkflowId, comparisonValue } = settings
    const comparisonLabel = getComparisonLabel(
      comparisonMode,
      this.comparisonModes()
    )

    const workflowLabel = firmWorkflowId
      ? getters.getFilterDropdownValueLabel('firm_workflow', firmWorkflowId)
      : I18n.t('firm_workflows.default_label')

    if (comparisonMode === CompareMixin.TYPES.SHOW_ALL) {
      return `${workflowLabel}`
    } else {
      return `${workflowLabel} ${comparisonLabel} ${comparisonValue || ''}`
    }
  },

  normalizeSettings(settings) {
    if (settings.comparisonMode === CompareMixin.TYPES.SHOW_ALL) {
      return {
        ...settings,
        comparisonValue: '',
      }
    }

    return settings
  },

  serializeSettings(settings) {
    return {
      firm_workflow_id: settings.firmWorkflowId,
      comparison_mode: settings.comparisonMode,
      comparison_value: settings.comparisonValue,
    }
  },

  deserializeSettings(params) {
    return {
      firmWorkflowId: params.firm_workflow_id,
      comparisonMode: params.comparison_mode,
      comparisonValue: params.comparison_value,
    }
  },
}

// filterType "model":
// - configurationComponent -> component that renders in filter modal
// - comparisonModes -> used to define comparison options for dropdown in modal
// - activeSubtitle -> method that renders subtitles in active filters list
// - normalizeSettings -> used to modify settings before sending them to server (e.g. clear some values)
// - serializeSettings/deserializeSettings -> step of stateToUrlParams()

const filterTypes = [
  {
    type: filters.COMPANY_DATA,
    name_key: 'client_attribute',
    ledgerRequired: false,

    comparisonModes() {
      return [
        ...CompareMixin.computed.textComparisonModes(),
        ...CompareMixin.computed.isComparisonMode(),
        ...CompareMixin.computed.isNotComparisonMode(),
        ...CompareMixin.computed.showAllComparisonMode(),
        ...CompareMixin.computed.booleanComparisonModes(),
      ]
    },

    getColumnTitle(settings) {
      return clientAttributeLabel(settings.filterDataType)
    },

    activeSubtitle(settings, getters) {
      const { comparisonMode, filterDataType, filterDataValue } = settings
      const comparisonLabel = getComparisonLabel(
        comparisonMode,
        this.comparisonModes()
      )

      const typeLabel = clientAttributeLabel(settings.filterDataType)

      if (comparisonMode === CompareMixin.TYPES.SHOW_ALL) {
        return `${typeLabel} ${comparisonLabel}`
      } else {
        const valueLabel = getters.getFilterDropdownValueLabel(
          filterDataType,
          filterDataValue
        )
        return `${typeLabel} ${comparisonLabel} ${valueLabel}`
      }
    },
    normalizeSettings(settings) {
      if (settings.comparisonMode === CompareMixin.TYPES.SHOW_ALL) {
        return {
          ...settings,
          filterDataValue: '',
        }
      }

      return settings
    },

    serializeSettings(settings) {
      return {
        comparison_mode: settings.comparisonMode,
        filter_data_type: settings.filterDataType,
        filter_data_value: settings.filterDataValue,
      }
    },

    deserializeSettings(params) {
      return {
        comparisonMode: params.comparison_mode,
        filterDataType: params.filter_data_type,
        filterDataValue: params.filter_data_value,
      }
    },
  },
  {
    type: filters.FORMULA,
    name_key: 'formula',
    ledgerRequired: true,

    comparisonModes() {
      return [
        ...CompareMixin.computed.numberComparisonModes(),
        ...CompareMixin.computed.showAllComparisonMode(),
      ]
    },

    activeSubtitle(settings) {
      const { formula, comparisonMode, comparisonValue } = settings
      const comparisonLabel = getComparisonLabel(
        comparisonMode,
        this.comparisonModes()
      )

      return `${formula} ${comparisonLabel} ${comparisonValue || ''}`
    },

    normalizeSettings(settings) {
      if (settings.comparisonMode === CompareMixin.TYPES.SHOW_ALL) {
        return {
          ...settings,
          comparisonValue: '',
        }
      }

      return settings
    },

    serializeSettings(settings) {
      return {
        comparison_mode: settings.comparisonMode,
        comparison_value: settings.comparisonValue,
        formula: settings.formula,
      }
    },

    deserializeSettings(params) {
      return {
        comparisonMode: params.comparison_mode,
        comparisonValue: params.comparison_value,
        formula: params.formula,
      }
    },
  },
  {
    type: filters.FOLLOW_COMPANIES,
    name_key: 'follow_companies',
    ledgerRequired: false,

    activeSubtitle(settings, getters) {
      if (settings.comparisonMode === CompareMixin.TYPES.CURRENT_USER) {
        return I18n.t(
          'companies_overview.filters.follow_companies.current_user'
        )
      }
      if (settings.comparisonMode === CompareMixin.TYPES.SHOW_ALL) {
        return I18n.t('companies_overview.filters.comparison_modes.show_all')
      }

      const user = getters.getUserById(parseInt(settings.comparisonValue))
      return user?.name || ''
    },

    normalizeSettings(settings) {
      let comparisonValue = ''

      // set comparisonValue to UserID when comparisonMode is USER
      if (settings.comparisonMode === CompareMixin.TYPES.USER) {
        comparisonValue = settings.comparisonValue
      }

      return {
        comparisonValue: comparisonValue,
        comparisonMode: settings.comparisonMode,
      }
    },

    serializeSettings(settings) {
      return {
        comparison_mode: settings.comparisonMode,
        comparison_value: settings.comparisonValue,
        current_user_id: Silverfin.currentUserId(),
      }
    },

    deserializeSettings(params) {
      return {
        comparisonMode: params.comparison_mode,
        comparisonValue: params.comparison_value,
      }
    },
  },
  {
    type: filters.BOOKYEAR_END,
    name_key: 'bookyear_end',
    ledgerRequired: false,

    comparisonModes() {
      return [
        ...CompareMixin.computed.isComparisonMode(),
        ...CompareMixin.computed.showAllComparisonMode(),
      ]
    },

    activeSubtitle(settings) {
      if (settings.comparisonMode === CompareMixin.TYPES.SHOW_ALL) {
        return I18n.t('companies_overview.filters.comparison_modes.show_all')
      }

      return `${settings.endDate}`
    },

    normalizeSettings(settings) {
      if (settings.comparisonMode === CompareMixin.TYPES.SHOW_ALL) {
        return {
          ...settings,
          endDate: null,
        }
      }

      return settings
    },

    serializeSettings(settings) {
      return {
        end_date: settings.endDate,
        comparison_mode: settings.comparisonMode,
      }
    },

    deserializeSettings(params) {
      return {
        endDate: params.end_date,
        comparisonMode: params.comparison_mode,
      }
    },
  },
  {
    type: filters.RECONCILIATION_RESULT,
    name_key: 'reconciliation_result',
    ledgerRequired: true,

    comparisonModes() {
      return {
        number: CompareMixin.computed.numberComparisonModes(),
        text: CompareMixin.computed.textComparisonModes(),
        checkbox: CompareMixin.computed.booleanComparisonModes(),
        other: CompareMixin.computed.showAllComparisonMode(),
      }
    },

    activeSubtitle(settings, getters) {
      const { identifier, comparisonMode, comparisonValue } = settings
      let reconciliationText
      if (settings.reconciliationTextId) {
        reconciliationText = getters.getReconciliationTextById(
          settings.reconciliationTextId
        )
      } else {
        reconciliationText = getters.getReconciliationTextByPartnerId(
          settings.partnerReconciliationTextId
        )
      }

      const flattenComparisonModes = _.flatten(_.values(this.comparisonModes()))
      const comparisonLabel = getComparisonLabel(
        comparisonMode,
        flattenComparisonModes
      )

      if (reconciliationText === undefined) {
        return ''
      } else {
        const name = reconciliationText.name
        return `${name}: ${identifier} ${comparisonLabel} ${comparisonValue}`
      }
    },

    normalizeSettings(settings) {
      if (
        [
          CompareMixin.TYPES.SHOW_ALL,
          CompareMixin.TYPES.TRUE,
          CompareMixin.TYPES.FALSE,
        ].includes(settings.comparisonMode)
      ) {
        return {
          ...settings,
          comparisonValue: '',
        }
      }

      return settings
    },

    serializeSettings(settings) {
      return {
        comparison_mode: settings.comparisonMode,
        comparison_value: settings.comparisonValue,
        reconciliation_text_id: settings.reconciliationTextId,
        partner_reconciliation_text_id: settings.partnerReconciliationTextId,
        identifier: settings.identifier,
      }
    },

    deserializeSettings(params) {
      return {
        comparisonMode: params.comparison_mode,
        comparisonValue: params.comparison_value,
        reconciliationTextId: params.reconciliation_text_id,
        partnerReconciliationTextId: params.partner_reconciliation_text_id,
        identifier: params.identifier,
      }
    },
  },
  { ...workflowProgressFilter },
  {
    type: filters.WORKFLOW_SHARING_STATUS,
    name_key: 'workflow_sharing_status',
    ledgerRequired: true,
    hasModalHelp: true,

    comparisonModes() {
      return [
        ...CompareMixin.computed.isComparisonMode({
          customLabel: I18n.t(
            'companies_overview.filters.workflow_sharing_status.is_shared'
          ),
        }),
        ...CompareMixin.computed.isNotComparisonMode({
          customLabel: I18n.t(
            'companies_overview.filters.workflow_sharing_status.is_not_shared'
          ),
        }),
        ...CompareMixin.computed.showAllComparisonMode({
          customLabel: I18n.t(
            'companies_overview.filters.workflow_sharing_status.show_all'
          ),
        }),
      ]
    },
    serializeSettings(settings) {
      const { comparisonMode, firmWorkflowId } = settings

      return {
        comparison_mode: comparisonMode,
        firm_workflow_id: firmWorkflowId,
      }
    },

    deserializeSettings(params) {
      return {
        comparisonMode: params.comparison_mode,
        firmWorkflowId: params.firm_workflow_id,
      }
    },

    activeSubtitle(settings, getters) {
      const { comparisonMode, firmWorkflowId } = settings

      const workflowLabel = firmWorkflowId
        ? getters.getFilterDropdownValueLabel('firm_workflow', firmWorkflowId)
        : I18n.t('firm_workflows.default_label')

      const comparisonModeLabel = this.comparisonModes().find(
        (mode) => mode.value === comparisonMode
      ).label

      return `${workflowLabel} ${comparisonModeLabel}`
    },
  },
  {
    type: filters.WORKFLOW_STATUS,
    name_key: 'workflow_status',
    ledgerRequired: true,
    hasModalHelp: true,

    disabledOnFirmLevel() {
      return !Silverfin.currentFirmFeatures.supports_workflow_statuses
    },

    comparisonModes() {
      return [
        ...CompareMixin.computed.showAllComparisonMode(),
        ...CompareMixin.computed.isComparisonMode(),
        ...CompareMixin.computed.isNotComparisonMode(),
        ...CompareMixin.computed.untilStatusComparisonMode(),
        ...CompareMixin.computed.fromStatusComparisonMode(),
      ]
    },
    activeSubtitle(settings, getters) {
      const { comparisonMode, firmWorkflowId, workflowStatusId } = settings

      const workflowLabel = firmWorkflowId
        ? getters.getFilterDropdownValueLabel('firm_workflow', firmWorkflowId)
        : I18n.t('firm_workflows.default_label')

      if (comparisonMode === CompareMixin.TYPES.SHOW_ALL) {
        return workflowLabel
      } else {
        const label = comparisonMode.replace('status', '')

        const status = getters.getFilterDropdownValueLabel(
          'workflow_status',
          workflowStatusId
        )

        return `${workflowLabel} ${label} ${status}`
      }
    },

    serializeSettings(settings) {
      return {
        firm_workflow_id: settings.firmWorkflowId,
        workflow_status_id: settings.workflowStatusId,
        comparison_mode: settings.comparisonMode,
      }
    },

    deserializeSettings(params) {
      return {
        firmWorkflowId: params.firm_workflow_id,
        workflowStatusId: params.workflow_status_id,
        comparisonMode: params.comparison_mode,
      }
    },
  },
  {
    type: filters.PERIOD_LOCKED,
    name_key: 'period_locked',
    ledgerRequired: true,

    comparisonModes() {
      return [
        ...CompareMixin.computed.booleanComparisonModes(),
        ...CompareMixin.computed.showAllComparisonMode(),
      ]
    },

    activeSubtitle(settings) {
      if (settings.comparisonValue === CompareMixin.TYPES.SHOW_ALL) {
        return `${I18n.t(
          'companies_overview.filters.period_locked.title'
        )}: ${I18n.t('companies_overview.filters.comparison_modes.show_all')}`
      } else if (settings.comparisonValue === CompareMixin.TYPES.TRUE) {
        return `${I18n.t(
          'companies_overview.filters.period_locked.title'
        )}: ${I18n.t('companies_overview.filters.comparison_modes.true')}`
      } else if (settings.comparisonValue === CompareMixin.TYPES.FALSE) {
        return `${I18n.t(
          'companies_overview.filters.period_locked.title'
        )}: ${I18n.t('companies_overview.filters.comparison_modes.false')}`
      }
    },

    serializeSettings(settings) {
      return {
        comparison_value: settings.comparisonValue,
      }
    },

    deserializeSettings(params) {
      return {
        comparisonValue: params.comparison_value,
      }
    },
  },
  {
    type: filters.SUBMISSION_DATA,
    name_key: 'submissions_data',
    ledgerRequired: true,

    comparisonModes() {
      return [
        ...CompareMixin.computed.numberComparisonModes(),
        ...CompareMixin.computed.isComparisonMode(),
        ...CompareMixin.computed.isNotComparisonMode(),
        ...CompareMixin.computed.showAllComparisonMode(),
      ]
    },

    activeSubtitle(settings) {
      const {
        comparisonMode,
        submissionType,
        filterDataType,
        filterDataValue,
      } = settings
      const comparisonLabel = getComparisonLabel(
        comparisonMode,
        this.comparisonModes()
      )
      let criteriaLabel = ''
      if (comparisonMode === CompareMixin.TYPES.SHOW_ALL) {
        criteriaLabel = I18n.t(
          'companies_overview.filters.comparison_modes.show_all'
        )
      } else {
        criteriaLabel = `${comparisonLabel} ${titleize(
          filterDataValue,
          filterDataType !== 'due_date'
        )}`
      }

      return `${titleize(submissionType)}: ${titleize(
        filterDataType
      )} ${criteriaLabel}`
    },

    normalizeSettings(settings) {
      if (settings.comparisonMode === CompareMixin.TYPES.SHOW_ALL) {
        return {
          ...settings,
          filterDataValue: null,
        }
      }

      return settings
    },

    serializeSettings(settings) {
      return {
        submission_type: settings.submissionType,
        filter_data_type: settings.filterDataType,
        filter_data_value: settings.filterDataValue,
        comparison_mode: settings.comparisonMode,
      }
    },

    deserializeSettings(params) {
      return {
        submissionType: params.submission_type,
        filterDataType: params.filter_data_type,
        filterDataValue: params.filter_data_value,
        comparisonMode: params.comparison_mode,
      }
    },
  },
  {
    type: filters.WORKFLOW_USAGE,
    name_key: 'workflow_usage',

    getColumnTitle(settings, getters) {
      const { firmWorkflowId } = settings

      return I18n.t('companies_overview.filters.workflow_usage.column_title', {
        workflow_name: getters.getFilterDropdownValueLabel(
          'firm_workflow',
          firmWorkflowId
        ),
      })
    },

    comparisonModes() {
      return [
        ...CompareMixin.computed.isComparisonMode({
          customLabel: I18n.t(
            'companies_overview.filters.workflow_usage.is_present'
          ),
        }),
        ...CompareMixin.computed.isNotComparisonMode({
          customLabel: I18n.t(
            'companies_overview.filters.workflow_usage.is_not_present'
          ),
        }),
        ...CompareMixin.computed.showAllComparisonMode(),
      ]
    },
    serializeSettings(settings) {
      const { comparisonMode, firmWorkflowId } = settings

      return {
        comparison_mode: comparisonMode,
        firm_workflow_id: firmWorkflowId,
      }
    },

    deserializeSettings(params) {
      return {
        comparisonMode: params.comparison_mode,
        firmWorkflowId: params.firm_workflow_id,
      }
    },

    activeSubtitle(settings, getters) {
      const { comparisonMode, firmWorkflowId } = settings

      const workflowLabel = getters.getFilterDropdownValueLabel(
        'firm_workflow',
        firmWorkflowId
      )
      // archived workflow
      if (!workflowLabel) {
        return
      }
      const comparisonModeLabel = this.comparisonModes().find(
        (mode) => mode.value === comparisonMode
      ).label

      return `${workflowLabel} ${comparisonModeLabel}`
    },
  },
  {
    type: filters.COMPANY_GROUPS,
    name_key: 'company_groups',
    ledgerRequired: false,

    shouldShow() {
      return window.Silverfin.currentContractFeatures?.supports_groups
    },

    activeSubtitle(settings, getters) {
      if (settings.comparisonMode === CompareMixin.TYPES.SHOW_ALL) {
        return I18n.t('companies_overview.filters.comparison_modes.show_all')
      }

      const group = getters.getFilterDropdownValueLabel(
        'groups',
        parseInt(settings.comparisonValue)
      )

      return group || ''
    },

    normalizeSettings(settings) {
      if (settings.comparisonMode === CompareMixin.TYPES.SHOW_ALL) {
        return {
          ...settings,
          comparisonValue: '',
        }
      }

      return settings
    },

    serializeSettings(settings) {
      return {
        comparison_mode: settings.comparisonMode,
        comparison_value: settings.comparisonValue,
      }
    },

    deserializeSettings(params) {
      return {
        comparisonMode: params.comparison_mode,
        comparisonValue: params.comparison_value,
      }
    },
  },
]

export default filterTypes
